import { debounce } from '@theme/utils';

/**
 * Collection filters and sorting (`<collection-facets>`), a progressive enhancement. Category chips and the
 * in-browser pager are in assets/category-chips.js.
 *
 * - `<collection-facets>` wraps a native GET `<form>` (filters from `collection.filters`, `sort_by`)
 *   that works without JS. With JS, every change re-renders the results through the Section Rendering
 *   API (`?section_id=`), swaps the parts marked `[data-facets-swap][id]` and updates the URL with
 *   `history.replaceState`. It also drives the mobile bottom sheet (`.cat-tools.open` + `.cat-ovl`).
 */

/**
 * Query string for the filter form. Empty values are dropped, and so is a price slider left at its
 * maximum (`data-max`), so an untouched slider does not count as an active filter.
 *
 * @param {HTMLFormElement} form
 * @returns {URLSearchParams}
 */
export function buildParams(form) {
  const skip = new Set();
  for (const range of form.querySelectorAll('input[type="range"][data-max]')) {
    const input = /** @type {HTMLInputElement} */ (range);
    if (Number(input.value) >= Number(input.dataset.max)) skip.add(input.name);
  }
  const params = new URLSearchParams();
  for (const [name, value] of new FormData(form)) {
    if (typeof value === 'string' && value !== '' && !skip.has(name)) params.append(name, value);
  }
  return params;
}

/**
 * Text shown next to the price slider: `allLabel` at the maximum, otherwise the amount formatted with
 * `template`, the shop's money format for 0 (e.g. `0 €` or `€0`).
 *
 * @param {number} value
 * @param {number} max
 * @param {string} template
 * @param {string} allLabel
 * @returns {string}
 */
export function priceLabel(value, max, template, allLabel) {
  // The slider moves continuously (step="any"); prices are shown, and filtered, in whole units.
  const whole = Math.ceil(value);
  return whole >= max ? allLabel : template.replace('0', String(whole));
}

/**
 * Filled part of the slider track, as a CSS percentage.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {string}
 */
export function rangePercent(value, min, max) {
  return `${+(((value - min) / Math.max(1, max - min)) * 100).toFixed(2)}%`;
}

/**
 * Cache key of a collection URL for the price slider's ceiling: path and filters, without the page, the sort
 * and the section id (none of them changes the highest price).
 *
 * @param {string} url
 * @returns {string}
 */
export function ceilingKey(url) {
  const { pathname, searchParams } = new URL(url, 'https://shop.invalid');
  for (const name of ['page', 'sort_by', 'section_id']) searchParams.delete(name);
  searchParams.sort();
  return `${pathname}?${searchParams}`;
}

/**
 * Copies the server-rendered parts into the live page: each `[data-facets-swap][id]` element of `root`
 * gets the attributes and content of its counterpart in `fresh` (the node itself is kept, so
 * `aria-live` regions announce the change). Inputs keep their node but take the fresh
 * `checked`/`disabled` state (counts at zero disable a value).
 *
 * @param {ParentNode} fresh - Parsed section HTML.
 * @param {ParentNode} root - Live section.
 */
export function swapParts(fresh, root) {
  for (const el of root.querySelectorAll('[data-facets-swap][id]')) {
    const source = fresh.querySelector(`#${CSS.escape(el.id)}`);
    if (!source) continue;
    for (const { name } of [...el.attributes]) if (!source.hasAttribute(name)) el.removeAttribute(name);
    for (const { name, value } of source.attributes) el.setAttribute(name, value);
    el.innerHTML = source.innerHTML;
  }
  for (const el of root.querySelectorAll('input[id]')) {
    const source = fresh.querySelector(`#${CSS.escape(el.id)}`);
    if (!(el instanceof HTMLInputElement) || !(source instanceof HTMLInputElement)) continue;
    el.checked = source.checked;
    el.disabled = source.disabled;
  }
}

export class CollectionFacets extends HTMLElement {
  /** @type {AbortController | undefined} */
  controller;

  /** Price ceilings already known, by {@link ceilingKey}. @type {Map<string, number>} */
  ceilings = new Map();

  /** Key of the ceiling being fetched, so a slower, older answer is ignored. @type {string | undefined} */
  ceilingFor;

  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    queueMicrotask(() => this.syncRange());
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.controller?.abort();
  }

  get form() {
    return this.querySelector('form');
  }

  get sheet() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.cat-tools'));
  }

  /** @param {Event} event */
  onInput = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.type === 'range') this.paintRange(input);
  };

  /** @param {Event} event */
  onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    if (!input.form || input.form !== this.form) return;
    if (input instanceof HTMLInputElement && input.type === 'checkbox') this.markGroup(input);
    if (input instanceof HTMLInputElement && input.type === 'range') {
      // Dragged smoothly (step="any"), filtered on the whole unit shown in the label.
      input.value = String(Math.min(Math.ceil(Number(input.value)), Number(input.max)));
      this.paintRange(input);
    }
    this.scheduleUpdate();
  };

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    event.preventDefault();
    this.update();
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const form = this.form;

    if (target.closest('[data-facets-open]')) {
      this.toggleSheet(true);
      return;
    }
    if (target.closest('[data-facets-close]')) {
      this.toggleSheet(false);
      return;
    }

    const clear = /** @type {HTMLElement | null} */ (target.closest('[data-facets-clear]'));
    if (clear && form) {
      event.preventDefault();
      const group = clear.closest('[role="group"]');
      for (const input of group?.querySelectorAll('input[type="checkbox"]') ?? []) {
        /** @type {HTMLInputElement} */ (input).checked = false;
      }
      clear.classList.add('is-on');
      this.update();
      return;
    }

    if (target.closest('[data-facets-reset]') && form) {
      event.preventDefault();
      for (const input of form.querySelectorAll('input')) {
        if (input.type === 'checkbox') input.checked = false;
        if (input.type === 'range') {
          input.value = input.dataset.max ?? input.max;
          this.paintRange(input);
        }
      }
      for (const all of form.querySelectorAll('[data-facets-clear]')) all.classList.add('is-on');
      this.update();
    }
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    if (event.key === 'Escape' && this.sheet?.classList.contains('open')) this.toggleSheet(false);
  };

  /**
   * Opens or closes the mobile filter sheet.
   *
   * @param {boolean} open
   */
  toggleSheet(open) {
    const sheet = this.sheet;
    if (!sheet || sheet.classList.contains('open') === open) return;
    const opener = /** @type {HTMLElement | null} */ (this.querySelector('[data-facets-open]'));
    sheet.classList.toggle('open', open);
    this.querySelector('.cat-ovl')?.classList.toggle('open', open);
    opener?.setAttribute('aria-expanded', String(open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) /** @type {HTMLElement | null} */ (sheet.querySelector('.cat-x'))?.focus({ preventScroll: true });
    else opener?.focus({ preventScroll: true });
  }

  /**
   * Updates the slider's value label and filled track.
   *
   * @param {HTMLInputElement} input
   */
  paintRange(input) {
    const value = Number(input.value);
    const max = Number(input.dataset.max ?? input.max);
    input.style.setProperty('--pct', rangePercent(value, Number(input.min), max));
    const out = input.parentElement?.querySelector('[data-price-out]');
    if (out instanceof HTMLElement) {
      out.textContent = priceLabel(value, max, out.dataset.template ?? '0', out.dataset.all ?? '');
    }
  }

  /**
   * « Tout » is pressed when no value of its group is checked.
   *
   * @param {HTMLInputElement} input
   */
  markGroup(input) {
    const group = input.closest('[role="group"]');
    const anyChecked = !!group?.querySelector('input[type="checkbox"]:checked');
    group?.querySelector('[data-facets-clear]')?.classList.toggle('is-on', !anyChecked);
  }

  /**
   * Keeps the price slider's maximum at the highest price the other filters leave (Shopify's `range_max` ignores
   * them). The section renders it while no price filter is active; with one, the matching products are capped by
   * it, so the ceiling comes from the page without the price filter (`data-max-url`), fetched once per filter set.
   *
   * @param {ParentNode} [fresh] - Freshly rendered section; the live page otherwise.
   */
  async syncRange(fresh) {
    const range = this.querySelector('input[type="range"][data-max]');
    if (!(range instanceof HTMLInputElement)) return;
    const source = fresh ? fresh.querySelector(`input[type="range"][name="${CSS.escape(range.name)}"]`) : range;
    if (!(source instanceof HTMLInputElement)) return;
    const maxUrl = source.dataset.maxUrl;
    if (!maxUrl) {
      this.ceilingFor = undefined;
      this.ceilings.set(ceilingKey(location.href), Number(source.dataset.max));
      this.setCeiling(range, Number(source.dataset.max));
      return;
    }
    const key = ceilingKey(maxUrl);
    this.ceilingFor = key;
    let max = this.ceilings.get(key);
    if (max === undefined) {
      try {
        const separator = maxUrl.includes('?') ? '&' : '?';
        const section = encodeURIComponent(this.dataset.section ?? '');
        const response = await fetch(`${maxUrl}${separator}section_id=${section}`);
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        max = Number(doc.querySelector('input[type="range"][data-max]')?.getAttribute('data-max'));
      } catch {
        return;
      }
      if (!(max > 0)) return;
      this.ceilings.set(key, max);
    }
    if (this.ceilingFor === key) this.setCeiling(range, max);
  }

  /**
   * Sets the slider's maximum. A slider left at its maximum (no price filter) follows it; a value above it is
   * brought down to it.
   *
   * @param {HTMLInputElement} range
   * @param {number} max
   */
  setCeiling(range, max) {
    if (!(max > 0)) return;
    const untouched = Number(range.value) >= Number(range.dataset.max ?? range.max);
    range.max = String(max);
    range.dataset.max = String(max);
    if (untouched || Number(range.value) > max) range.value = String(max);
    this.paintRange(range);
  }

  scheduleUpdate = debounce(() => this.update(), 250);

  async update() {
    const form = this.form;
    if (!form) return;
    const query = buildParams(form).toString();
    const url = `${location.pathname}${query ? `?${query}` : ''}`;
    const sectionUrl = `${url}${query ? '&' : '?'}section_id=${encodeURIComponent(this.dataset.section ?? '')}`;

    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(sectionUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      const html = await response.text();
      const focusedId = document.activeElement?.id;
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      swapParts(fresh, this);
      if (focusedId && document.activeElement === document.body) document.getElementById(focusedId)?.focus();
      history.replaceState(history.state, '', url);
      this.syncRange(fresh);
    } catch {
      if (controller.signal.aborted) return;
      // Section Rendering failed: fall back to a regular page load, which always works.
      location.assign(url);
    } finally {
      if (this.controller === controller) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('collection-facets')) customElements.define('collection-facets', CollectionFacets);
